/**
 * portalVisitClassifier.js — Iter-355 Guest Portal My Visit classifier.
 *
 * The Iter-353 upcoming-calendar + Iter-354 responsive layer share the
 * same rule for "what goes where":
 *
 *   • Upcoming Calendar Grid = active bookings for TODAY and the future.
 *     Status ∈ {booked, in_progress} AND Bali-local start date >= today.
 *   • Past Visits List       = everything else (Closed / Cancelled /
 *     No-Show, OR any earlier date that never became active).
 *
 * The moment an appointment is closed (`completed`), cancelled
 * (`cancelled`), or no-showed (`no_show`) it MUST leave the top calendar
 * and appear in the bottom list on the next repaint. Because the classifier
 * is pure and deterministic, any refetch (WebSocket-driven, event-driven,
 * or focus-driven) is enough to re-classify — there is no cached bucket to
 * invalidate.
 *
 * This file is INTENTIONALLY NOT an engine. The eight sealed engines
 * (`PackageEngine`, `CheckInEngine`, `BillingEngine`, `AuthEngine`,
 * `ReportingEngine`, `NotificationEngine`, `RosterEngine`,
 * `ClientPortalEngine`) are locked; this classifier lives alongside them
 * as an ordinary utility so the "hands-off" mandate on the engines is
 * preserved. Applies globally to all 8 branches.
 *
 * Pure JavaScript — no React, no state, no I/O, no DOM.
 */

/** Statuses that keep an appointment in the top Calendar Grid. */
export const ACTIVE_STATUSES = Object.freeze(["booked", "in_progress"]);

/**
 * Statuses that force an appointment into the Past Visits list regardless
 * of its date. "Closed" in the user's spec maps to appointment `completed`
 * (per SYSTEM_RULES.md Iter-344: only Close Order flips an appointment to
 * `completed`, and a `completed` appointment leaves the live grid).
 */
export const HISTORICAL_STATUSES = Object.freeze(["completed", "cancelled", "no_show"]);

const _ACTIVE = new Set(ACTIVE_STATUSES);
const _HISTORICAL = new Set(HISTORICAL_STATUSES);

/** Bali-local YYYY-MM-DD for "today". Deterministic, timezone-independent. */
export function baliTodayKey(now = Date.now()) {
  try {
    return new Date(now).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
  } catch {
    return "";
  }
}

/** Bali-local YYYY-MM-DD for a given ISO. Matches the engine's baliDateKey. */
export function apptBaliDateKey(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
  } catch {
    return "";
  }
}

/**
 * Classify a single appointment as "upcoming" (Calendar Grid) or "past"
 * (Past Visits List) per the Iter-355 rules.
 *
 * Priority order:
 *   1. Historical status (completed / cancelled / no_show) → "past".
 *   2. Bali-local start date STRICTLY before today (Bali) → "past" —
 *      catches abandoned "booked" rows that never got closed.
 *   3. Active status (booked / in_progress) → "upcoming".
 *   4. Anything unrecognised → "past" (defensive).
 *
 * The now-source is injectable so unit tests are deterministic.
 */
export function classifyMyVisit(appt, now = Date.now()) {
  const status = (appt && appt.status) || "";
  if (_HISTORICAL.has(status)) return "past";
  const dk = apptBaliDateKey(appt && appt.start_iso);
  const today = baliTodayKey(now);
  if (!dk || (today && dk < today)) return "past";
  if (_ACTIVE.has(status)) return "upcoming";
  return "past";
}

/**
 * Filter every "upcoming" appointment out of a list. Sort soonest-first
 * so the calendar's date columns render in ascending order downstream.
 */
export function deriveActiveVisits(appts, now = Date.now()) {
  const list = Array.isArray(appts) ? appts : [];
  const out = list.filter((a) => classifyMyVisit(a, now) === "upcoming");
  return out.sort((x, y) => {
    const xt = new Date(x?.start_iso || 0).getTime();
    const yt = new Date(y?.start_iso || 0).getTime();
    return (Number.isFinite(xt) ? xt : 0) - (Number.isFinite(yt) ? yt : 0);
  });
}

/**
 * Filter every "past" appointment. Sort newest-first so the list-view
 * cards render most-recent-at-top.
 */
export function derivePastVisits(appts, now = Date.now()) {
  const list = Array.isArray(appts) ? appts : [];
  const out = list.filter((a) => classifyMyVisit(a, now) === "past");
  return out.sort((x, y) => {
    const xt = new Date(x?.start_iso || 0).getTime();
    const yt = new Date(y?.start_iso || 0).getTime();
    return (Number.isFinite(yt) ? yt : 0) - (Number.isFinite(xt) ? xt : 0);
  });
}
